import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Search, Plus, Edit2, Trash2, Mail, Globe2, Phone, MapPin, X, User, UserCheck, Upload, MessageCircle } from "lucide-react";
import Modal from "@/components/Modal";
import SupplierForm from "@/components/SupplierForm";
import ImportSuppliersModal from "@/components/ImportSuppliersModal";
import SendMessageModal from "@/components/SendMessageModal";
import { toast } from "sonner";

const firstEmail = (c) => (c?.emails && c.emails[0]) || "";
const firstPhone = (c) => (c?.phones && c.phones[0]) || "";

const colorForName = (name) => {
  const colors = ["#C85A17", "#A6470F", "#1E3A5F", "#2F4F4F", "#6B6965", "#7C2D12"];
  const c = name?.charCodeAt(0) || 0;
  return colors[c % colors.length];
};

function Avatar({ name, size = 36 }) {
  const initials = (name || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: 6, background: colorForName(name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontFamily: "Cabinet Grotesk", fontSize: size * 0.4, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

const PAGE_SIZE = 25;

function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push("…");
    result.push(sorted[i]);
  }
  return result;
}

export default function Suppliers() {
  const { isAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [assignTarget, setAssignTarget] = useState("");
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterCountryDebounced, setFilterCountryDebounced] = useState("");
  const [modal, setModal] = useState(null); // 'add' | 'edit' | 'import' | 'email' | 'whatsapp'
  const [activeSupplier, setActiveSupplier] = useState(null);
  const [detail, setDetail] = useState(null);
  const [activities, setActivities] = useState([]);
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [pageJump, setPageJump] = useState("");
  const skipPageScroll = useRef(true);

  const reloadActivity = async (id) => {
    const r = await api.get("/activities", { params: { customer_id: id } });
    setActivities(r.data);
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilterCountryDebounced(filterCountry);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterCountry]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        search: searchDebounced.trim(),
      };
      if (filterSource !== "all") params.source = filterSource;
      if (filterCountryDebounced.trim()) params.country = filterCountryDebounced.trim();
      const { data } = await api.get("/suppliers", { params });
      setSuppliers(data.items || []);
      setTotal(data.total ?? 0);
      setSources(data.sources || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load suppliers");
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced, filterSource, filterCountryDebounced]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setSalesUsers(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    if (!detail) { setActivities([]); return; }
    reloadActivity(detail.id);
  }, [detail]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (!loading && suppliers.length === 0 && page > 1 && total > 0) {
      setPage(page - 1);
    }
  }, [loading, suppliers.length, page, total]);

  useEffect(() => {
    if (skipPageScroll.current) {
      skipPageScroll.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [safePage]);

  const goToPage = (raw) => {
    const n = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n) || n < 1) return;
    setPage(Math.min(pageCount, Math.max(1, n)));
    setPageJump("");
  };

  const pageIds = suppliers.map((c) => c.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleSelectPage = () => setSelected((s) =>
    allPageSelected ? s.filter((id) => !pageIds.includes(id)) : [...new Set([...s, ...pageIds])]
  );

  const onBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Remove ${selected.length} supplier${selected.length > 1 ? "s" : ""}? This also deletes their notes & activity.`)) return;
    try {
      await api.post("/suppliers/bulk-delete", { ids: selected });
      toast.success(`Removed ${selected.length} supplier${selected.length > 1 ? "s" : ""}`);
      if (detail && selected.includes(detail.id)) setDetail(null);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete suppliers");
    }
  };

  const onBulkAssign = async () => {
    if (selected.length === 0) return;
    const target = salesUsers.find((u) => u.id === assignTarget);
    const label = target ? target.name : "Unassigned";
    if (!window.confirm(`Assign ${selected.length} supplier${selected.length > 1 ? "s" : ""} to ${label}?`)) return;
    try {
      await api.post("/suppliers/bulk-assign", { ids: selected, sales_person_id: assignTarget });
      toast.success(`Assigned ${selected.length} supplier${selected.length > 1 ? "s" : ""} to ${label}`);
      if (detail && selected.includes(detail.id)) {
        const { data } = await api.get(`/suppliers/${detail.id}`);
        setDetail(data);
      }
      setSelected([]);
      setAssignTarget("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not assign suppliers");
    }
  };

  const onAdd = async (form) => {
    try {
      await api.post("/suppliers", form);
      toast.success("Supplier added");
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add supplier");
    }
  };

  const onEdit = async (form) => {
    try {
      await api.patch(`/suppliers/${activeSupplier.id}`, form);
      toast.success("Updated");
      setModal(null);
      setActiveSupplier(null);
      if (detail?.id === activeSupplier.id) {
        const { data } = await api.get(`/suppliers/${activeSupplier.id}`);
        setDetail(data);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update supplier");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Remove this supplier? This will also delete its notes & activity.")) return;
    await api.delete(`/suppliers/${id}`);
    toast.success("Removed");
    if (detail?.id === id) setDetail(null);
    setSelected((s) => s.filter((x) => x !== id));
    load();
  };

  const addNote = async () => {
    if (!note.trim() || !detail) return;
    await api.post("/activities", { customer_id: detail.id, type: "note", message: note.trim() });
    setNote("");
    reloadActivity(detail.id);
    toast.success("Note added");
  };

  return (
    <div data-testid="suppliers-page">
      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-4" style={{ padding: "10px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 }} data-testid="bulk-actions">
          <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.length} selected</span>
          <button onClick={() => setSelected([])} className="btn-ghost" style={{ fontSize: 12 }}>Clear</button>
          {isAdmin && (
            <div className="flex items-center gap-2 ml-auto">
              <select
                value={assignTarget}
                onChange={(e) => setAssignTarget(e.target.value)}
                className="input-base"
                style={{ padding: "7px 10px", fontSize: 13, width: "auto" }}
                data-testid="bulk-assign-select"
              >
                <option value="">— Unassigned —</option>
                {salesUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button onClick={onBulkAssign} className="btn-outline" data-testid="bulk-assign-btn">
                <UserCheck size={14} /> Assign selected
              </button>
            </div>
          )}
          <button onClick={onBulkDelete} className="btn-outline" style={{ color: "var(--danger)", borderColor: "var(--danger)", marginLeft: isAdmin ? 0 : "auto" }} data-testid="bulk-delete-btn">
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center mb-6">
        <div style={{ position: "relative", flex: "1 1 280px", maxWidth: 420 }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-2)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers…" className="input-base" style={{ paddingLeft: 36 }} data-testid="suppliers-search" />
        </div>
        <div className="flex gap-2 items-center" data-testid="country-filter">
          <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Country:</span>
          <input
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            placeholder="Search country…"
            className="input-base"
            style={{ padding: "7px 10px", fontSize: 13, width: 160 }}
            data-testid="filter-country"
          />
        </div>
        {sources.length > 0 && (
          <div className="flex gap-2 items-center" data-testid="source-filter">
            <span style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>Sourced from:</span>
            <select value={filterSource} onChange={(e) => { setFilterSource(e.target.value); setPage(1); }} className="input-base" style={{ padding: "7px 10px", fontSize: 13, width: "auto" }} data-testid="filter-source">
              <option value="all">All</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
        <div className="ml-auto flex gap-2">
          <button onClick={() => setModal("import")} className="btn-outline" data-testid="import-suppliers-btn">
            <Upload size={15} /> Import CSV
          </button>
          <button onClick={() => setModal("add")} className="btn-primary" data-testid="add-supplier-btn">
            <Plus size={15} /> Add Supplier
          </button>
        </div>
      </div>

      {/* Count */}
      {!loading && (
        <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 12 }} data-testid="suppliers-count">
          <strong style={{ color: "var(--text)", fontWeight: 700 }}>{total}</strong> {total === 1 ? "supplier" : "suppliers"}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="loading-state">
          <div style={{ fontSize: 14 }}>Loading suppliers…</div>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="card-ledger" style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }} data-testid="empty-state">
          <div style={{ fontSize: 14 }}>No suppliers match your filters.</div>
        </div>
      ) : (
        <div className="card-ledger overflow-hidden" data-testid="suppliers-list">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--surface-2)" }}>
                <th style={{ padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)", width: 40 }}>
                  <input type="checkbox" checked={allPageSelected} onChange={toggleSelectPage} title="Select all on this page" data-testid="select-all-checkbox" style={{ cursor: "pointer" }} />
                </th>
                {["Company", "Contact", "Country / City", "Dealing In", "Quality", "Sales Person", ""].map((h, i) => (
                  <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {suppliers.map((c, i) => (
                <tr
                  key={c.id}
                  onClick={() => setDetail(c)}
                  className="stagger"
                  style={{ borderBottom: "1px solid var(--border)", cursor: "pointer", animationDelay: `${i * 30}ms` }}
                  data-testid={`supplier-row-${c.id}`}
                >
                  <td style={{ padding: "14px 16px" }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} data-testid={`select-checkbox-${c.id}`} style={{ cursor: "pointer" }} />
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <div className="flex items-center gap-3">
                      <Avatar name={c.company} size={36} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{c.company}</div>
                        <div style={{ fontSize: 12, color: "var(--text-2)" }}>{c.dealing_in?.slice(0, 60) || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13 }}>
                    <div>{c.contact || "—"}</div>
                    {firstEmail(c) && <div style={{ fontSize: 11, color: "var(--text-2)" }}>{firstEmail(c)}</div>}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>
                    <div>{c.country || "—"}</div>
                    {c.city_or_state && <div style={{ fontSize: 11 }}>{c.city_or_state}</div>}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)", maxWidth: 160 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.dealing_in || "—"}</div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{c.quality || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{c.sales_person_name || "—"}</td>
                  <td style={{ padding: "14px 16px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setActiveSupplier(c); setModal("email"); }} className="btn-ghost" title="Send Email" data-testid={`email-btn-${c.id}`} disabled={!firstEmail(c)}>
                        <Mail size={14} />
                      </button>
                      <button onClick={() => { setActiveSupplier(c); setModal("whatsapp"); }} className="btn-ghost" title="Send WhatsApp" data-testid={`whatsapp-btn-${c.id}`} disabled={!firstPhone(c)} style={{ color: firstPhone(c) ? "#25D366" : undefined }}>
                        <MessageCircle size={14} />
                      </button>
                      <button onClick={() => { setActiveSupplier(c); setModal("edit"); }} className="btn-ghost" title="Edit" data-testid={`edit-btn-${c.id}`}>
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => onDelete(c.id)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete" data-testid={`delete-btn-${c.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="pagination">
          <div style={{ fontSize: 13, color: "var(--text-2)" }}>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn-outline" disabled={safePage <= 1} data-testid="page-prev">Prev</button>
            {pageNumbers(safePage, pageCount).map((p, i) => (
              typeof p === "number" ? (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={p === safePage ? "btn-primary" : "btn-outline"}
                  style={{ minWidth: 36, padding: "6px 10px", fontSize: 13 }}
                  data-testid={`page-num-${p}`}
                >
                  {p}
                </button>
              ) : (
                <span key={`ellipsis-${i}`} style={{ fontSize: 13, color: "var(--text-2)", padding: "0 4px" }}>{p}</span>
              )
            ))}
            <button onClick={() => setPage((p) => Math.min(pageCount, p + 1))} className="btn-outline" disabled={safePage >= pageCount} data-testid="page-next">Next</button>
            <form
              onSubmit={(e) => { e.preventDefault(); goToPage(pageJump); }}
              className="flex items-center gap-2"
              style={{ marginLeft: 4 }}
              data-testid="page-jump-form"
            >
              <span style={{ fontSize: 13, color: "var(--text-2)", whiteSpace: "nowrap" }}>Go to</span>
              <input
                type="number"
                min={1}
                max={pageCount}
                value={pageJump}
                onChange={(e) => setPageJump(e.target.value)}
                placeholder={String(safePage)}
                className="input-base"
                style={{ width: 64, padding: "6px 8px", fontSize: 13 }}
                data-testid="page-jump-input"
              />
              <button type="submit" className="btn-outline" style={{ fontSize: 13 }} data-testid="page-jump-btn">Go</button>
            </form>
          </div>
        </div>
      )}

      {/* Detail slide-over */}
      {detail && (
        <>
          <div onClick={() => setDetail(null)} style={{ position: "fixed", inset: 0, background: "rgba(28,27,26,0.4)", zIndex: 150 }} className="fade-in" />
          <aside className="slide-in" style={{ position: "fixed", top: 0, right: 0, width: "min(560px, 100%)", height: "100vh", background: "var(--surface)", zIndex: 160, borderLeft: "1px solid var(--border)", display: "flex", flexDirection: "column" }} data-testid="supplier-detail-panel">
            <div className="flex items-start justify-between px-6 py-5 divider-h">
              <div className="flex gap-4 items-center min-w-0">
                <Avatar name={detail.company} size={48} />
                <div style={{ minWidth: 0 }}>
                  <div className="font-display" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>{detail.company}</div>
                  <div style={{ fontSize: 13, color: "var(--text-2)" }}>{detail.contact}</div>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="btn-ghost" data-testid="detail-close"><X size={18} /></button>
            </div>

            <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1 }}>
              <div className="overline" style={{ fontSize: 10, marginBottom: 8 }}>Contact</div>
              <div className="grid gap-2 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {[
                  ...(detail.emails || []).map((e) => [Mail, e, `mailto:${e}`]),
                  ...(detail.phones || []).map((p) => [Phone, p, `tel:${p}`]),
                  [Globe2, detail.website, detail.website ? `https://${detail.website.replace(/^https?:\/\//, "")}` : null],
                  [MapPin, detail.country, null],
                  [User, detail.sales_person_name, null],
                ].map(([Icon, val, href], i) => val ? (
                  <a key={i} href={href || undefined} target={href ? "_blank" : undefined} rel="noreferrer" className="flex items-center gap-2" style={{ fontSize: 13, color: href ? "var(--primary)" : "var(--text)", textDecoration: "none" }}>
                    <Icon size={14} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val}</span>
                  </a>
                ) : null)}
              </div>

              {/* Profile */}
              {(detail.rating > 0 || detail.address || detail.sourced_from || detail.supplier_since || detail.city_or_state || detail.dealing_in || detail.quality) && (
                <>
                  <div className="overline" style={{ fontSize: 10, marginBottom: 8 }}>Profile</div>
                  <div className="grid gap-2 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
                    {detail.city_or_state && (
                      <div style={{ fontSize: 13 }}><span style={{ color: "var(--text-2)" }}>City / State:</span> {detail.city_or_state}</div>
                    )}
                    {detail.rating > 0 && (
                      <div className="flex items-center gap-2" style={{ fontSize: 13 }}>
                        <span style={{ color: "var(--text-2)" }}>Rating:</span>
                        <span style={{ color: "var(--primary)", letterSpacing: 2 }}>{"★".repeat(detail.rating)}<span style={{ color: "var(--border)" }}>{"★".repeat(5 - detail.rating)}</span></span>
                      </div>
                    )}
                    {detail.sourced_from && (
                      <div style={{ fontSize: 13 }}><span style={{ color: "var(--text-2)" }}>Sourced from:</span> {detail.sourced_from}</div>
                    )}
                    {detail.supplier_since && (
                      <div style={{ fontSize: 13 }}><span style={{ color: "var(--text-2)" }}>Supplier since:</span> {detail.supplier_since}</div>
                    )}
                    {detail.address && (
                      <div style={{ fontSize: 13, gridColumn: "1 / -1" }}><span style={{ color: "var(--text-2)" }}>Address:</span> {detail.address}</div>
                    )}
                    {detail.dealing_in && (
                      <div style={{ fontSize: 13, gridColumn: "1 / -1" }}><span style={{ color: "var(--text-2)" }}>Dealing in:</span> {detail.dealing_in}</div>
                    )}
                    {detail.quality && (
                      <div style={{ fontSize: 13, gridColumn: "1 / -1" }}><span style={{ color: "var(--text-2)" }}>Quality:</span> {detail.quality}</div>
                    )}
                  </div>
                </>
              )}

              {/* Notes & activity */}
              <div className="overline mt-6 mb-3" style={{ fontSize: 10 }}>Activity & Notes</div>
              <div className="flex gap-2 mb-3">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a quick note…" className="input-base" data-testid="note-input" onKeyDown={(e) => e.key === "Enter" && addNote()} />
                <button onClick={addNote} className="btn-outline" data-testid="note-add">Add</button>
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {activities.map((a) => (
                  <li key={a.id} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 6, height: 6, borderRadius: 6, background: "var(--primary)", marginTop: 8, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>{a.message}</div>
                      <div style={{ fontSize: 11, color: "var(--text-2)", marginTop: 2 }}>{a.type} · {new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-2 justify-between px-6 py-4" style={{ borderTop: "1px solid var(--border)" }}>
              <button onClick={() => { setActiveSupplier(detail); setModal("edit"); }} className="btn-outline" data-testid="detail-edit">
                <Edit2 size={14} /> Edit
              </button>
              <div className="flex gap-2">
                <button onClick={() => { setActiveSupplier(detail); setModal("email"); }} className="btn-outline" disabled={!firstEmail(detail)} title={firstEmail(detail) ? "Send email" : "Add an email first"} data-testid="detail-email">
                  <Mail size={14} /> Email
                </button>
                <button onClick={() => { setActiveSupplier(detail); setModal("whatsapp"); }} className="btn-outline" disabled={!firstPhone(detail)} title={firstPhone(detail) ? "Send WhatsApp" : "Add a phone first"} data-testid="detail-whatsapp" style={{ color: firstPhone(detail) ? "#25D366" : undefined, borderColor: firstPhone(detail) ? "#25D366" : undefined }}>
                  <MessageCircle size={14} /> WhatsApp
                </button>
              </div>
            </div>
          </aside>
        </>
      )}

      {modal === "add" && (
        <Modal title="Add Supplier" subtitle="Capture vendor details" onClose={() => setModal(null)} size="lg">
          <SupplierForm onSave={onAdd} onCancel={() => setModal(null)} />
        </Modal>
      )}

      {modal === "edit" && activeSupplier && (
        <Modal title={`Edit · ${activeSupplier.company}`} onClose={() => { setModal(null); setActiveSupplier(null); }} size="lg">
          <SupplierForm initial={activeSupplier} onSave={onEdit} onCancel={() => { setModal(null); setActiveSupplier(null); }} submitLabel="Save Changes" />
        </Modal>
      )}

      {modal === "import" && (
        <ImportSuppliersModal
          onClose={() => setModal(null)}
          onImported={(opts) => { load(); if (!opts?.keepOpen) setModal(null); }}
        />
      )}

      {(modal === "email" || modal === "whatsapp") && activeSupplier && (
        <SendMessageModal
          channel={modal}
          customer={activeSupplier}
          kind="supplier"
          onClose={() => { setModal(null); setActiveSupplier(null); }}
          onSent={() => { if (detail) reloadActivity(detail.id); }}
        />
      )}
    </div>
  );
}
